import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  FileText,
  Download,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Calendar,
  Lightbulb,
  Star,
  User,
  Film,
  Clock,
  MessageSquare,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react'
import { evaluationsApi } from '../../lib/api/evaluations'
import { sessionsApi } from '../../lib/api/sessions'
import { downloadHtmlAsPdf } from '../../lib/utils/htmlToPdf'
import { buildRecordingSrc } from '../../lib/utils/streamUrl'
import { parseVtt } from '../../lib/utils/parseVtt'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import EmptyState from '../../components/ui/EmptyState'
import Modal from '../../components/ui/Modal'
import { useProfileTimezone } from '../../lib/hooks/useProfileTimezone'
import { formatDateInTz, formatTimeInTz } from '../../lib/utils/timezone'

interface FeedbackItem {
  id: number
  session_id?: number
  booking_id?: number
  interviewer_id: number
  status: string
  communication_score?: number
  technical_score?: number
  problem_solving_score?: number
  leadership_score?: number
  cultural_fit_score?: number
  overall_score?: number
  strengths: string[]
  improvements: string[]
  practice_actions: string[]
  red_flags: string[]
  summary?: string
  interview_readiness?: string
  key_insight?: string
  confidence_delivery_summary?: string
  performance_breakdown_notes?: Record<string, { went_well: string; needs_work: string; example: string }>
  approved_at?: string
  delivered_at?: string
  created_at: string
  updated_at: string
  session_title?: string
  scheduled_at?: string
  interviewer_name?: string
}

const BREAKDOWN_THEME_DEFS = [
  { slug: 'communication',            label: 'Communication',            scoreKey: 'communication_score' },
  { slug: 'content_quality',          label: 'Content Quality',          scoreKey: 'technical_score' },
  { slug: 'confidence_presence',      label: 'Confidence & Presence',    scoreKey: 'leadership_score' },
  { slug: 'role_alignment',           label: 'Role Alignment',           scoreKey: 'cultural_fit_score' },
  { slug: 'thinking_problem_solving', label: 'Thinking & Problem Solving', scoreKey: 'problem_solving_score' },
] as const

function readinessLabel(r: string) {
  if (r === 'strong') return 'Strong'
  if (r === 'developing') return 'Developing'
  if (r === 'needs_work') return 'Needs Work'
  return r
}



// ── Types for per-question bundle (mirrors GET /api/feedback/:id/questions) ──

interface QuestionBundleCategory { key: string; label: string; display_order: number }
interface QuestionBundleResponse {
  scores: Record<string, number>
  note: string | null
  recommendation: string | null
  completion_status: string
}
interface QuestionBundleItem {
  question_index: number
  question_text: string
  question_type_name: string | null
  categories: QuestionBundleCategory[]
  response: QuestionBundleResponse | null
}
interface QuestionBundle {
  total: number
  completed: number
  in_progress: number
  items: QuestionBundleItem[]
}

function scoreLabel(n: number): string {
  return ({ 1: 'Very Weak', 2: 'Weak', 3: 'Mixed', 4: 'Strong', 5: 'Very Strong' } as Record<number, string>)[n] ?? '—'
}

function scorePillColorByScore(n: number): { bg: string; color: string } {
  if (n >= 4) return { bg: '#dcfce7', color: '#15803d' }
  if (n === 3) return { bg: '#fef3c7', color: '#92400e' }
  return { bg: '#fee2e2', color: '#b91c1c' }
}

function renderQuestionProgressHtml(bundle: QuestionBundle | null): string {
  if (!bundle || bundle.total === 0) return ''

  const items = bundle.items
    .map((it, idx) => {
      const scores = it.response?.scores ?? {}
      const note = (it.response?.note ?? '').trim()
      const rec = (it.response?.recommendation ?? '').trim()

      // Derive what worked (score ≥ 4) and what could improve (score ≤ 3)
      const sortedCats = it.categories.slice().sort((a, b) => a.display_order - b.display_order)
      const workedItems = sortedCats.filter((c) => {
        const v = scores[c.key]; return typeof v === 'number' && v >= 4
      })
      const improveItems = sortedCats.filter((c) => {
        const v = scores[c.key]; return typeof v === 'number' && v <= 3
      })

      const recColors: Record<string, { bg: string; color: string }> = {
        keep:    { bg: '#dcfce7', color: '#15803d' },
        improve: { bg: '#fef3c7', color: '#92400e' },
        replace: { bg: '#fee2e2', color: '#b91c1c' },
      }
      const recColor = rec ? (recColors[rec] ?? { bg: '#f3e8ff', color: '#6b21a8' }) : null
      const recLabel: Record<string, string> = { keep: 'Keep Answer', improve: 'Improve Answer', replace: 'Replace Answer' }

      const typeColors: Record<string, { bg: string; color: string }> = {
        'behavioral':      { bg: '#dbeafe', color: '#1d4ed8' },
        'situational':     { bg: '#e0e7ff', color: '#4338ca' },
        'role-specific':   { bg: '#fce7f3', color: '#be185d' },
        'tell me about yourself': { bg: '#ecfdf5', color: '#059669' },
        'motivation / fit':{ bg: '#fef3c7', color: '#92400e' },
        'test question':   { bg: '#f1f5f9', color: '#64748b' },
      }
      const typeKey = (it.question_type_name ?? '').toLowerCase()
      const typeColor = Object.entries(typeColors).find(([k]) => typeKey.includes(k))?.[1] ?? { bg: '#f3e8ff', color: '#7c3aed' }

      return `<div style="margin-top:${idx === 0 ? 0 : 14}px;border:1px solid #e2e8f0;border-radius:8px;padding:12px 14px;page-break-inside:avoid;break-inside:avoid;background:#fafbfc;">

        <!-- Question header -->
        <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;">
          <span style="display:inline-block;min-width:24px;height:24px;border-radius:50%;background:#1e2432;color:white;font-size:10px;font-weight:700;text-align:center;line-height:24px;flex-shrink:0;">${idx + 1}</span>
          <div style="flex:1;">
            ${it.question_type_name ? `<div style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;background:${typeColor.bg};color:${typeColor.color};margin-bottom:4px;">${it.question_type_name}</div>` : ''}
            <div style="font-size:11px;font-weight:700;color:#1e2432;line-height:1.4;">${it.question_text}</div>
          </div>
          ${recColor ? `<span style="display:inline-block;padding:2px 10px;border-radius:10px;font-size:8px;font-weight:700;white-space:nowrap;background:${recColor.bg};color:${recColor.color};flex-shrink:0;">${recLabel[rec] ?? rec}</span>` : ''}
        </div>

        <!-- What worked / what could improve / suggested approach -->
        <table style="width:100%;border-collapse:collapse;margin-top:6px;">
          <tr>
            <td style="width:33%;padding-right:8px;vertical-align:top;">
              <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:#16a34a;margin-bottom:4px;padding-bottom:2px;border-bottom:1px solid #bbf7d0;">What Worked</div>
              ${workedItems.length > 0
                ? workedItems.map((c) => {
                    const v = scores[c.key]
                    const cs = scorePillColorByScore(v)
                    return `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:3px;"><span style="font-size:9px;color:#14532d;">${c.label}</span><span style="font-size:8px;font-weight:600;padding:1px 6px;border-radius:8px;background:${cs.bg};color:${cs.color};">${scoreLabel(v)}</span></div>`
                  }).join('')
                : `<p style="font-size:9px;color:#9ca3af;font-style:italic;margin:0;">—</p>`
              }
            </td>
            <td style="width:33%;padding:0 4px;vertical-align:top;border-left:1px solid #e2e8f0;padding-left:8px;">
              <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:#d97706;margin-bottom:4px;padding-bottom:2px;border-bottom:1px solid #fde68a;">What Could Improve</div>
              ${improveItems.length > 0
                ? improveItems.map((c) => {
                    const v = scores[c.key]
                    const cs = scorePillColorByScore(v)
                    return `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:3px;"><span style="font-size:9px;color:#78350f;">${c.label}</span><span style="font-size:8px;font-weight:600;padding:1px 6px;border-radius:8px;background:${cs.bg};color:${cs.color};">${scoreLabel(v)}</span></div>`
                  }).join('')
                : `<p style="font-size:9px;color:#9ca3af;font-style:italic;margin:0;">—</p>`
              }
            </td>
            <td style="width:33%;padding-left:8px;vertical-align:top;border-left:1px solid #e2e8f0;">
              <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:#4338ca;margin-bottom:4px;padding-bottom:2px;border-bottom:1px solid #c7d2fe;">Suggested Approach</div>
              ${note
                ? `<p style="font-size:9px;color:#1e3a8a;margin:0;line-height:1.4;">${note}</p>`
                : `<p style="font-size:9px;color:#9ca3af;font-style:italic;margin:0;">No notes added</p>`
              }
            </td>
          </tr>
        </table>

      </div>`
    })
    .join('')

  return `<div style="font-size:9px;color:#4b5563;margin-bottom:10px;">
    ${bundle.completed} of ${bundle.total} questions evaluated${bundle.in_progress > 0 ? ` · ${bundle.in_progress} in progress` : ''}
  </div>
  ${items}`
}

const buildPdfHtml = (item: FeedbackItem, bundle: QuestionBundle | null): string => {
  const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'

  const stripEmoji = (s: string) =>
    s.replace(/[\u{10000}-\u{10FFFF}]|[✂-➰]|[︀-️]|‍/gu, '')
  const esc = (s: string) =>
    stripEmoji(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  const safe = (fn: () => string): string => { try { return fn() } catch { return '' } }
  const nl2br = (s: string) => esc(s).replace(/\n/g, '<br>')

  const pill = (text: string, bg: string, color: string) =>
    `<span style="display:inline-block;padding:2px 10px;border-radius:10px;font-size:9px;font-weight:700;background:${bg};color:${color};">${text}</span>`

  const listItems = (arr: string[], borderColor: string) =>
    arr.length
      ? arr.map((s) => `<div style="padding:6px 10px 6px 12px;margin:4px 0;border-left:3px solid ${borderColor};border-radius:0 4px 4px 0;background:#f8f9fb;font-size:10px;line-height:1.5;">${esc(s)}</div>`).join('')
      : ''

  const sectionHeading = (num: string, title: string, color: string) =>
    `<div style="display:flex;align-items:center;gap:10px;margin:20px 0 10px;padding-bottom:6px;border-bottom:2px solid ${color};">
      <span style="display:inline-block;width:22px;height:22px;border-radius:50%;background:${color};color:white;font-size:10px;font-weight:700;text-align:center;line-height:22px;flex-shrink:0;">${num}</span>
      <span style="font-size:13px;font-weight:700;color:${color};">${title}</span>
    </div>`

  // ── Section 1: Overview ───────────────────────────────────────────────────

  const readinessMap: Record<string, { label: string; bg: string; color: string; border: string }> = {
    strong:    { label: '✓ Strong',      bg: '#dcfce7', color: '#15803d', border: '#86efac' },
    developing:{ label: '~ Developing',  bg: '#fef9c3', color: '#a16207', border: '#fde047' },
    needs_work:{ label: '! Needs Work',  bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
  }

  const readinessEntry = item.interview_readiness ? readinessMap[item.interview_readiness] : null
  const readinessHtml = readinessEntry
    ? `<div style="margin-bottom:10px;padding:10px 14px;background:#eff6ff;border-left:4px solid #3b82f6;border-radius:0 6px 6px 0;">
        <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#1d4ed8;margin-bottom:3px;">Interview Readiness</div>
        <p style="font-size:10px;color:#1e3a8a;margin:0;line-height:1.6;">${readinessEntry.label}</p>
      </div>`
    : ''

  const topStrengths = (item.strengths ?? []).slice(0, 3)
  const topImprovements = (item.improvements ?? []).slice(0, 3)

  const strengthsImproveHtml = (topStrengths.length > 0 || topImprovements.length > 0)
    ? `<table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
        <tr>
          <td style="width:50%;padding-right:10px;vertical-align:top;">
            <div style="font-size:9px;font-weight:700;color:#16a34a;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Top Strengths</div>
            ${listItems(topStrengths, '#22c55e')}
            ${topStrengths.length === 0 ? '<p style="font-size:9px;color:#9ca3af;font-style:italic;margin:0;">None recorded</p>' : ''}
          </td>
          <td style="width:50%;padding-left:10px;vertical-align:top;border-left:1px solid #e2e8f0;">
            <div style="font-size:9px;font-weight:700;color:#d97706;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Areas to Improve</div>
            ${listItems(topImprovements, '#f59e0b')}
            ${topImprovements.length === 0 ? '<p style="font-size:9px;color:#9ca3af;font-style:italic;margin:0;">None recorded</p>' : ''}
          </td>
        </tr>
      </table>`
    : ''

  const keyInsightHtml = item.key_insight
    ? `<div style="margin-bottom:10px;padding:10px 14px;background:#eff6ff;border-left:4px solid #3b82f6;border-radius:0 6px 6px 0;">
        <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#1d4ed8;margin-bottom:3px;">Key Insight</div>
        <p style="font-size:10px;color:#1e3a8a;margin:0;line-height:1.6;">${nl2br(item.key_insight)}</p>
      </div>`
    : ''

  const confidenceHtml = item.confidence_delivery_summary
    ? `<div style="margin-bottom:10px;padding:10px 14px;background:#fdf4ff;border-left:4px solid #a855f7;border-radius:0 6px 6px 0;">
        <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#7e22ce;margin-bottom:3px;">Confidence &amp; Delivery</div>
        <p style="font-size:10px;color:#581c87;margin:0;line-height:1.6;">${nl2br(item.confidence_delivery_summary)}</p>
      </div>`
    : ''

  const summaryHtml = item.summary
    ? `<div style="padding:10px 14px;background:#f7fafc;border:1px solid #e2e8f0;border-radius:6px;">
        <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:#64748b;margin-bottom:4px;">Summary</div>
        <p style="font-size:10px;color:#1a202c;margin:0;line-height:1.7;word-break:break-word;">${nl2br(item.summary)}</p>
      </div>`
    : ''

  const section1 = safe(() => readinessHtml + keyInsightHtml + confidenceHtml + strengthsImproveHtml + summaryHtml)

  // ── Section 2: Performance Breakdown ─────────────────────────────────────

  const bdNotes = item.performance_breakdown_notes
  const bdThemes = [
    { slug: 'communication',            label: 'Communication',            scoreKey: 'communication_score' },
    { slug: 'content_quality',          label: 'Content Quality',          scoreKey: 'technical_score' },
    { slug: 'confidence_presence',      label: 'Confidence &amp; Presence', scoreKey: 'leadership_score' },
    { slug: 'role_alignment',           label: 'Role Alignment',           scoreKey: 'cultural_fit_score' },
    { slug: 'thinking_problem_solving', label: 'Thinking &amp; Problem Solving', scoreKey: 'problem_solving_score' },
  ]

  const section2 = safe(() => bdNotes
    ? bdThemes.map((t) => {
        const n = bdNotes[t.slug]
        if (!n || (!n.went_well && !n.needs_work && !n.example)) return ''
        return `<div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:10px;page-break-inside:avoid;break-inside:avoid;">
          <div style="display:flex;align-items:center;padding:8px 12px;background:#f8fafc;border-bottom:1px solid #e2e8f0;">
            <span style="font-size:11px;font-weight:700;color:#1e2432;">${t.label}</span>
          </div>
          <table style="width:100%;border-collapse:collapse;">
            <tr>
              ${n.went_well ? `<td style="width:33%;padding:8px 10px;vertical-align:top;border-right:1px solid #e2e8f0;"><div style="font-size:8px;font-weight:700;color:#16a34a;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px;">What Went Well</div><p style="font-size:9px;color:#14532d;margin:0;line-height:1.5;">${esc(n.went_well)}</p></td>` : '<td></td>'}
              ${n.needs_work ? `<td style="width:33%;padding:8px 10px;vertical-align:top;border-right:1px solid #e2e8f0;"><div style="font-size:8px;font-weight:700;color:#d97706;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px;">What Needs Work</div><p style="font-size:9px;color:#78350f;margin:0;line-height:1.5;">${esc(n.needs_work)}</p></td>` : '<td></td>'}
              ${n.example ? `<td style="width:33%;padding:8px 10px;vertical-align:top;"><div style="font-size:8px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px;">Example</div><p style="font-size:9px;color:#334155;margin:0;line-height:1.5;font-style:italic;">${esc(n.example)}</p></td>` : '<td></td>'}
            </tr>
          </table>
        </div>`
      }).join('')
    : '')

  // ── Section 3: Q-by-Q Analysis ────────────────────────────────────────────

  const section3 = safe(() => renderQuestionProgressHtml(bundle))

  // ── Section 4: Red Flags ──────────────────────────────────────────────────

  const section4 = safe(() => (item.red_flags ?? []).length > 0
    ? `<div style="padding:8px 12px;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;margin-bottom:10px;">
        <p style="font-size:9px;color:#92400e;margin:0;line-height:1.5;"><strong>Human Observation Only</strong> — the following concerns were noted by your interviewer directly. This is not AI-generated.</p>
      </div>
      ${(item.red_flags ?? []).map((s) => `<div style="padding:7px 10px 7px 14px;margin:5px 0;border-left:3px solid #f59e0b;border-radius:0 6px 6px 0;background:#fffbeb;font-size:10px;color:#78350f;line-height:1.5;">${esc(s)}</div>`).join('')}`
    : '')

  // ── Section 5: Practice Actions ───────────────────────────────────────────

  const section5 = safe(() => (item.practice_actions ?? []).length > 0
    ? (item.practice_actions ?? []).map((s, i) => `<div style="display:flex;align-items:flex-start;gap:10px;padding:8px 10px;margin:5px 0;border:1px solid #bfdbfe;border-radius:6px;background:#eff6ff;page-break-inside:avoid;">
        <span style="display:inline-block;min-width:20px;height:20px;border-radius:50%;background:#2563eb;color:white;font-size:9px;font-weight:700;text-align:center;line-height:20px;flex-shrink:0;">${i + 1}</span>
        <p style="font-size:10px;color:#1e3a8a;margin:0;line-height:1.5;">${esc(s)}</p>
      </div>`).join('')
    : '')

  // ── Section 6: Supporting Materials ──────────────────────────────────────

  const section6 = `<div style="padding:10px 14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;font-size:10px;color:#475569;">
    <p style="margin:0 0 4px;font-weight:600;">Attached to this session:</p>
    <ul style="margin:0;padding-left:16px;line-height:1.8;">
      <li>Session Recording — available in your dashboard</li>
      <li>Session Transcript — available in your dashboard</li>
    </ul>
  </div>`

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <title>${item.session_title || 'Feedback Report'}</title>
  <style>
    *{box-sizing:border-box;}
    body{font-family:Arial,sans-serif;color:#1a202c;margin:0;padding:28px 32px;font-size:10px;line-height:1.5;max-width:760px;}
    p{margin:0;word-break:break-word;overflow-wrap:break-word;}
  </style>
  </head><body>

  <!-- Report header -->
  <div style="padding-bottom:14px;border-bottom:2px solid #1e2432;margin-bottom:4px;">
    <div style="font-size:18px;font-weight:800;color:#1e2432;margin-bottom:4px;">${esc(item.session_title || 'Interview Feedback Report')}</div>
    <div style="display:flex;flex-wrap:wrap;gap:16px;font-size:10px;color:#64748b;">
      <span>${formatDate(item.scheduled_at || item.delivered_at || item.created_at)}</span>
      ${item.interviewer_name ? `<span>Interviewer: ${esc(item.interviewer_name)}</span>` : ''}
      ${item.overall_score != null ? `<span>Overall Score: <strong style="color:#1e2432;">${item.overall_score.toFixed(1)} / 5.0</strong></span>` : ''}
    </div>
  </div>
  <div style="margin-bottom:18px;">${pill('Feedback Received', '#ccfbf1', '#065f46')}</div>

  ${section1 ? sectionHeading('1', 'Overview', '#1e2432') + section1 : ''}
  ${section2 ? sectionHeading('2', 'Performance Breakdown', '#7c3aed') + section2 : ''}
  ${section3 ? sectionHeading('3', 'Question-by-Question Analysis', '#4338ca') + section3 : ''}
  ${section4 ? sectionHeading('4', 'Interviewer Observations', '#b45309') + section4 : ''}
  ${section5 ? sectionHeading('5', 'Practice Actions &amp; Next Steps', '#0369a1') + section5 : ''}
  ${sectionHeading('6', 'Supporting Materials', '#64748b') + section6}

  </body></html>`
}

const handleDownloadPdf = async (item: FeedbackItem) => {
  let bundle: QuestionBundle | null = null
  try {
    bundle = await evaluationsApi.getClientQuestionBundle(item.id)
  } catch {
    // Non-critical: PDF still works without this section
  }
  const html = buildPdfHtml(item, bundle)
  const title = (item.session_title || 'Feedback-Report').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '-')
  const dateStr = new Date(item.scheduled_at || item.delivered_at || item.created_at).toISOString().slice(0, 10)
  const filename = `${title}-${dateStr}.pdf`
  await downloadHtmlAsPdf(html, filename)
}


export default function ClientFeedback() {
  const [searchParams] = useSearchParams()
  const timezone = useProfileTimezone()
  const [feedbackList, setFeedbackList] = useState<FeedbackItem[]>([])
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [questionBundles, setQuestionBundles] = useState<Record<number, QuestionBundle | null>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Recording modal
  const [recordingData, setRecordingData] = useState<{
    recording_url: string; transcript_url: string | null; duration_seconds: number | null
  } | null>(null)
  const [recordingOpen, setRecordingOpen] = useState(false)
  const [recordingLoading, setRecordingLoading] = useState(false)
  const [recordingError, setRecordingError] = useState('')

  // Transcript modal
  const [transcriptOpen, setTranscriptOpen] = useState(false)
  const [transcriptCues, setTranscriptCues] = useState<{ time: string; speaker: string; text: string }[]>([])
  const [transcriptLoading, setTranscriptLoading] = useState(false)
  const [transcriptError, setTranscriptError] = useState('')

  const openRecording = async (item: FeedbackItem) => {
    setRecordingOpen(true)
    setRecordingData(null)
    setRecordingError('')
    setRecordingLoading(true)
    try {
      const res = item.booking_id
        ? await sessionsApi.getBookingRecording(item.booking_id)
        : await sessionsApi.getRecording(item.session_id!)
      const data = res.data ?? res
      setRecordingData({ ...data, duration_seconds: null })
    } catch {
      setRecordingError('Recording is not available yet. Please check back after the session ends.')
    } finally {
      setRecordingLoading(false)
    }
  }

  const openTranscript = async (item: FeedbackItem) => {
    setTranscriptOpen(true)
    setTranscriptCues([])
    setTranscriptError('')
    setTranscriptLoading(true)
    try {
      const vttText = item.booking_id
        ? await sessionsApi.getBookingTranscript(item.booking_id)
        : await sessionsApi.getSessionTranscript(item.session_id!)
      const cues = parseVtt(vttText)
      if (cues.length === 0) setTranscriptError('No transcript content available.')
      else setTranscriptCues(cues)
    } catch {
      setTranscriptError('Transcript is not available for this session.')
    } finally {
      setTranscriptLoading(false)
    }
  }

  const closeRecording = () => { setRecordingOpen(false); setRecordingData(null); setRecordingError('') }
  const closeTranscript = () => { setTranscriptOpen(false); setTranscriptCues([]); setTranscriptError('') }

  const loadFeedback = () => {
    setLoading(true)
    setError('')
    evaluationsApi
      .getFeedback()
      .then((data) => {
        const items = Array.isArray(data) ? data : data?.data ?? []
        setFeedbackList(items)
      })
      .catch((err) => {
        setError(err?.message || 'Failed to load feedback. Please try again.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadFeedback() }, [])

  useEffect(() => {
    if (feedbackList.length === 0) return
    const feedbackId = Number(searchParams.get('feedbackId'))
    if (feedbackId) {
      const match = feedbackList.find((item) => item.id === feedbackId)
      if (match) setExpandedId(match.id)
      return
    }
    const source = searchParams.get('source')
    const id = Number(searchParams.get('id'))
    if (!source || !id) return
    const match = feedbackList.find((item) =>
      source === 'booking' ? item.booking_id === id : item.session_id === id
    )
    if (match) setExpandedId(match.id)
  }, [feedbackList, searchParams])

  const toggleExpand = (id: number) => {
    const next = expandedId === id ? null : id
    setExpandedId(next)
    if (next && !(next in questionBundles)) {
      evaluationsApi.getClientQuestionBundle(next)
        .then((b) => setQuestionBundles((prev) => ({ ...prev, [next]: b })))
        .catch(() => setQuestionBundles((prev) => ({ ...prev, [next]: null })))
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A'
    return formatDateInTz(dateStr, timezone)
  }

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return ''
    return formatTimeInTz(dateStr, timezone)
  }

  if (loading) return <LoadingSpinner message="Loading feedback..." />

  return (
    <div className="client-feedback">
      <div className="page-header">
        <h1 className="page-title">View Feedback</h1>
        <p className="page-subtitle">
          Review detailed, structured feedback from your practice interviews
        </p>
      </div>

      {error && (
        <div className="cfb-error-banner">
          <span>{error}</span>
          <button type="button" onClick={loadFeedback} className="cfb-retry-btn">
            Try Again
          </button>
        </div>
      )}

      {!error && feedbackList.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No Feedback Reports Yet"
          message="Complete an interview session to receive detailed feedback from your interviewer."
        />
      ) : (
        <div className="cfb-list">
          {feedbackList.map((item) => {
            const isExpanded = expandedId === item.id
            const title = item.session_title || 'Interview Session'

            return (
              <div key={item.id} className="cfb-card">
                {/* ── Header ── */}
                <div
                  className="cfb-header"
                  onClick={() => toggleExpand(item.id)}
                >
                  <div className="cfb-header-left">
                    <h3 className="cfb-title">{title}</h3>
                    <div className="cfb-meta">
                      <span className="cfb-meta-item">
                        <Calendar size={13} />
                        {formatDate(item.scheduled_at || item.delivered_at || item.created_at)}
                      </span>
                      {(item.scheduled_at || item.delivered_at || item.created_at) && (
                        <span className="cfb-meta-item">
                          <Clock size={13} />
                          {formatTime(item.scheduled_at || item.delivered_at || item.created_at)}
                        </span>
                      )}
                      {item.interviewer_name && (
                        <span className="cfb-meta-item">
                          <User size={13} />
                          {item.interviewer_name}
                        </span>
                      )}
                      {item.overall_score && (
                        <span className="cfb-meta-item">
                          <Star size={13} />
                          Overall: {item.overall_score.toFixed(1)} / 5.0
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="cfb-header-right">
                    <span className="cfb-badge-delivered">
                      <CheckCircle size={13} />
                      Feedback Received
                    </span>

                    <div className="cfb-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="cfb-download-btn" title="View Recording" type="button" onClick={() => openRecording(item)}>
                        <Film size={15} />
                      </button>
                      <button className="cfb-download-btn" title="View Transcript" type="button" onClick={() => openTranscript(item)}>
                        <MessageSquare size={15} />
                      </button>
                      <button className="cfb-download-btn" title="Download PDF Report" type="button" onClick={() => handleDownloadPdf(item)}>
                        <Download size={15} />
                      </button>
                    </div>

                    <button className="cfb-expand-btn" type="button">
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>
                </div>

                {/* ── Expanded content ── */}
                {isExpanded && (
                  <div className="cfb-body">

                    {/* ── Section 1: Overview ─────────────────────────── */}
                    <div className="cfb-section">
                      <div className="cfb-section-label">
                        <Star size={14} />
                        Overview
                      </div>

                      {/* Readiness badge */}
                      {item.interview_readiness && (
                        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#eff6ff', borderLeft: '3px solid #3b82f6', borderRadius: '0 8px 8px 0' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#1d4ed8', display: 'block', marginBottom: 4 }}>Interview Readiness</span>
                          <p style={{ fontSize: 14, color: '#1e3a8a', margin: 0, lineHeight: 1.6 }}>{readinessLabel(item.interview_readiness)}</p>
                        </div>
                      )}

                      {/* Key Insight */}
                      {item.key_insight && (
                        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#eff6ff', borderLeft: '3px solid #3b82f6', borderRadius: '0 8px 8px 0' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#1d4ed8', display: 'block', marginBottom: 4 }}>Key Insight</span>
                          <p style={{ fontSize: 14, color: '#1e3a8a', margin: 0, lineHeight: 1.6 }}>{item.key_insight}</p>
                        </div>
                      )}

                      {/* Confidence summary */}
                      {item.confidence_delivery_summary && (
                        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fdf4ff', borderLeft: '3px solid #a855f7', borderRadius: '0 8px 8px 0' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#7e22ce', display: 'block', marginBottom: 4 }}>Confidence &amp; Delivery</span>
                          <p style={{ fontSize: 14, color: '#581c87', margin: 0, lineHeight: 1.6 }}>{item.confidence_delivery_summary}</p>
                        </div>
                      )}

                      {/* Top strengths + areas to improve side by side */}
                      {(item.strengths.length > 0 || item.improvements.length > 0) && (
                        <div className="cfb-overview-cols">
                          {item.strengths.length > 0 && (
                            <div>
                              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#16a34a', display: 'block', marginBottom: 8 }}>Top Strengths</span>
                              <ul className="cfb-list-items">
                                {item.strengths.slice(0, 3).map((s, i) => (
                                  <li key={i} className="cfb-list-item cfb-item-green">{s}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {item.improvements.length > 0 && (
                            <div>
                              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#d97706', display: 'block', marginBottom: 8 }}>Areas to Improve</span>
                              <ul className="cfb-list-items">
                                {item.improvements.slice(0, 3).map((s, i) => (
                                  <li key={i} className="cfb-list-item cfb-item-amber">{s}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Session summary */}
                      {item.summary && (
                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Summary</span>
                          <p className="cfb-summary-text">{item.summary}</p>
                        </div>
                      )}
                    </div>

                    {/* ── Section 2: Performance Breakdown ────────────── */}
                    {item.performance_breakdown_notes && BREAKDOWN_THEME_DEFS.some((t) => {
                      const n = item.performance_breakdown_notes![t.slug]
                      return n && (n.went_well || n.needs_work || n.example)
                    }) && (
                      <div className="cfb-section">
                        <div className="cfb-section-label" style={{ background: 'rgba(124,58,237,0.08)', color: '#7c3aed' }}>
                          <TrendingUp size={14} />
                          Performance Breakdown
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          {BREAKDOWN_THEME_DEFS.map((theme) => {
                            const notes = item.performance_breakdown_notes![theme.slug]
                            if (!notes || (!notes.went_well && !notes.needs_work && !notes.example)) return null
                            return (
                              <div key={theme.slug} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', background: '#fafbfc' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--primary-navy)' }}>{theme.label}</span>
                                </div>
                                <div className="cfb-breakdown-cols">
                                  {notes.went_well && (
                                    <div style={{ padding: '8px 12px', background: '#f0fdf4', borderLeft: '3px solid #22c55e', borderRadius: '0 6px 6px 0' }}>
                                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#16a34a', display: 'block', marginBottom: 3 }}>What Went Well</span>
                                      <p style={{ fontSize: 13, color: '#14532d', margin: 0, lineHeight: 1.5 }}>{notes.went_well}</p>
                                    </div>
                                  )}
                                  {notes.needs_work && (
                                    <div style={{ padding: '8px 12px', background: '#fffbeb', borderLeft: '3px solid #f59e0b', borderRadius: '0 6px 6px 0' }}>
                                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#d97706', display: 'block', marginBottom: 3 }}>What Needs Work</span>
                                      <p style={{ fontSize: 13, color: '#78350f', margin: 0, lineHeight: 1.5 }}>{notes.needs_work}</p>
                                    </div>
                                  )}
                                  {notes.example && (
                                    <div style={{ padding: '8px 12px', background: '#f8fafc', borderLeft: '3px solid #94a3b8', borderRadius: '0 6px 6px 0' }}>
                                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#64748b', display: 'block', marginBottom: 3 }}>Example</span>
                                      <p style={{ fontSize: 13, color: '#334155', margin: 0, lineHeight: 1.5, fontStyle: 'italic' }}>{notes.example}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* ── Section 4: Red Flags (Interviewer Observations) ── */}
                    {item.red_flags && item.red_flags.length > 0 && (
                      <div className="cfb-section" style={{ borderLeft: '3px solid #f59e0b' }}>
                        <div className="cfb-section-label" style={{ background: 'rgba(245,158,11,0.1)', color: '#b45309' }}>
                          <AlertTriangle size={14} />
                          Interviewer Observations
                        </div>
                        <p style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
                          The following observations are from your interviewer — not AI-generated.
                        </p>
                        <ul className="cfb-list-items">
                          {item.red_flags.map((s, i) => (
                            <li key={i} className="cfb-list-item" style={{ borderLeft: '3px solid #f59e0b', background: 'rgba(245,158,11,0.04)' }}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* ── Section 5: Next Steps ────────────────────────── */}
                    {item.practice_actions.length > 0 && (
                      <div className="cfb-section">
                        <div className="cfb-section-label cfb-label-navy">
                          <Lightbulb size={14} />
                          Practice Actions &amp; Next Steps
                        </div>
                        <ul className="cfb-list-items">
                          {item.practice_actions.map((s, i) => (
                            <li key={i} className="cfb-list-item cfb-item-navy">{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* ── Section 6: Supporting Materials ─────────────── */}
                    <div className="cfb-section">
                      <div className="cfb-section-label">
                        <FileText size={14} />
                        Supporting Materials
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                        <button className="btn btn-outline" type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }} onClick={() => openRecording(item)}>
                          <Film size={14} /> View Recording
                        </button>
                        <button className="btn btn-outline" type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }} onClick={() => openTranscript(item)}>
                          <MessageSquare size={14} /> View Transcript
                        </button>
                        <button className="btn btn-outline" type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }} onClick={() => handleDownloadPdf(item)}>
                          <Download size={14} /> Download PDF Report
                        </button>
                      </div>
                    </div>

                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ── Transcript Modal ── */}
      <Modal
        open={transcriptOpen}
        onClose={closeTranscript}
        title="Session Transcript"
        maxWidth={680}
        footer={<button className="btn btn-outline" onClick={closeTranscript}>Close</button>}
      >
        {transcriptLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
            Loading transcript…
          </div>
        )}
        {!transcriptLoading && transcriptError && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
            <MessageSquare size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ fontSize: 14 }}>{transcriptError}</p>
          </div>
        )}
        {!transcriptLoading && transcriptCues.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', borderRadius: 10 }}>
            {transcriptCues.map((cue, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, padding: '12px 16px', borderBottom: i < transcriptCues.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#7c3aed', whiteSpace: 'nowrap', paddingTop: 2, minWidth: 42 }}>
                  {cue.time}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                  {cue.speaker && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                      padding: '2px 7px', borderRadius: 4, width: 'fit-content',
                      background: cue.speaker.toLowerCase() === 'interviewer' ? 'rgba(37,99,235,0.1)' : 'rgba(22,163,74,0.1)',
                      color: cue.speaker.toLowerCase() === 'interviewer' ? '#2563eb' : '#16a34a',
                    }}>
                      {cue.speaker}
                    </span>
                  )}
                  <p style={{ fontSize: 13, color: 'var(--text-primary)', margin: 0, lineHeight: 1.5 }}>{cue.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* ── Recording Modal ── */}
      <Modal
        open={recordingOpen}
        onClose={closeRecording}
        title="Session Recording"
        maxWidth={720}
        footer={<button className="btn btn-outline" onClick={closeRecording}>Close</button>}
      >
        {recordingLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
            Loading recording…
          </div>
        )}
        {!recordingLoading && recordingError && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px', textAlign: 'center' }}>
            <Film size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
            <p style={{ fontWeight: 600, marginBottom: 6 }}>Recording Not Available</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{recordingError}</p>
          </div>
        )}
        {!recordingLoading && recordingData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <video
              className="cfb-video"
              style={{ width: '100%', borderRadius: 10, background: '#000', outline: 'none' }}
              src={buildRecordingSrc(recordingData.recording_url) ?? undefined}
              controls
              preload="metadata"
              onDurationChange={(e) => {
                const d = Math.round((e.target as HTMLVideoElement).duration)
                if (d && isFinite(d)) {
                  setRecordingData((prev) => prev ? { ...prev, duration_seconds: d } : prev)
                }
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              {recordingData.duration_seconds != null && (
                <p style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                  <Clock size={13} />
                  Duration: {Math.floor(recordingData.duration_seconds / 60)}m {String(recordingData.duration_seconds % 60).padStart(2, '0')}s
                </p>
              )}
              <a
                href={buildRecordingSrc(recordingData.recording_url, { download: true }) ?? '#'}
                download="session-recording.mp4"
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
              >
                <Download size={14} /> Download Recording
              </a>
            </div>
          </div>
        )}
      </Modal>

      <style>{`
        .cfb-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .cfb-card {
          background: white;
          border-radius: 14px;
          border: 1px solid var(--border);
          overflow: hidden;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
          transition: box-shadow 0.2s;
        }

        .cfb-card:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.09);
        }

        /* Header */
        .cfb-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          cursor: pointer;
          transition: background 0.15s;
          gap: 16px;
        }

        .cfb-header:hover {
          background: #f9fafb;
        }

        .cfb-header-left {
          flex: 1;
          min-width: 0;
        }

        .cfb-title {
          font-size: 16px;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 6px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .cfb-meta {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 14px;
        }

        .cfb-meta-item {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12.5px;
          color: var(--text-muted);
        }

        .cfb-header-right {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }

        .cfb-badge-delivered {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          background: rgba(34,197,94,0.1);
          color: #16a34a;
          white-space: nowrap;
        }

        .cfb-actions {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .cfb-download-btn {
          width: 34px;
          height: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: white;
          color: var(--primary-navy);
          cursor: pointer;
          transition: all 0.15s;
          flex-shrink: 0;
        }

        .cfb-download-btn:hover {
          border-color: var(--primary-navy);
          background: rgba(45,55,72,0.05);
        }

        .cfb-expand-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #6b7280;
          transition: all 0.15s;
        }

        .cfb-expand-btn:hover {
          border-color: var(--primary-navy);
          color: var(--primary-navy);
        }

        /* Body */
        .cfb-body {
          border-top: 1px solid var(--border);
          background: #f8f9fb;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        /* Sections */
        .cfb-section {
          background: white;
          border-radius: 10px;
          border: 1px solid var(--border);
          padding: 18px 20px;
          min-width: 0;
          overflow: hidden;
        }

        .cfb-section-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 5px 10px;
          border-radius: 6px;
          background: rgba(45,55,72,0.07);
          color: var(--primary-navy);
          margin-bottom: 14px;
          max-width: 100%;
        }

        .cfb-label-green {
          background: rgba(34,197,94,0.1);
          color: #16a34a;
        }

        .cfb-label-amber {
          background: rgba(245,158,11,0.1);
          color: #d97706;
        }

        .cfb-label-navy {
          background: rgba(45,55,72,0.08);
          color: var(--primary-navy);
        }

        /* Summary */
        .cfb-section-summary {
          border-left: 3px solid var(--primary-navy);
        }

        .cfb-summary-text {
          font-size: 14px;
          line-height: 1.75;
          color: var(--text-dark);
          margin: 0;
          overflow-wrap: break-word;
          word-break: break-word;
        }

        /* List items */
        .cfb-list-items {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .cfb-list-item {
          padding: 10px 14px;
          border-radius: 8px;
          border: 1px solid var(--border);
          font-size: 13.5px;
          color: var(--text-dark);
          line-height: 1.6;
        }

        .cfb-item-green {
          border-left: 3px solid #22c55e;
          background: rgba(34,197,94,0.03);
        }

        .cfb-item-amber {
          border-left: 3px solid #f59e0b;
          background: rgba(245,158,11,0.03);
        }

        .cfb-item-navy {
          border-left: 3px solid var(--primary-navy);
          background: rgba(45,55,72,0.03);
        }

        .cfb-overview-cols {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-top: 12px;
        }

        .cfb-breakdown-cols {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 8px;
        }

        @media (max-width: 600px) {
          .cfb-overview-cols { grid-template-columns: 1fr; }
          .cfb-breakdown-cols { grid-template-columns: 1fr; }
        }

        .cfb-error-banner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 14px 20px;
          background: #fef2f2;
          border: 1px solid #fca5a5;
          border-radius: 10px;
          color: #dc2626;
          font-size: 14px;
          margin-bottom: 20px;
        }
        .cfb-retry-btn {
          padding: 6px 16px;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
        }
        .cfb-retry-btn:hover { background: #b91c1c; }

        .cfb-video {
          max-height: 400px;
        }

        @media (max-width: 768px) {
          .cfb-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .cfb-header-right {
            width: 100%;
            justify-content: space-between;
          }

          .cfb-video {
            max-height: 220px;
          }

          /* hide badge on very narrow screens to save space */
          .cfb-badge-delivered {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .cfb-header {
            padding: 14px 16px;
          }
          .cfb-body {
            padding: 14px 16px;
          }
          .cfb-meta {
            gap: 8px;
          }
        }
      `}</style>
    </div>
  )
}
